// ─────────────────────────────────────────────────────────────────────────────
// País de operación del usuario autenticado.
//
// Todo el catálogo geográfico de la app (regiones/comunas, labels, moneda,
// formato de números) dependía de Chile hard-codeado. Este contexto resuelve el
// país real del operador (o del comercio) y lo expone como única fuente para
// esos datos.
//
// Cascada de resolución (§3.1 del plan de expansión a Argentina):
//   1. user.logisticCompany.operationsCountryId  (claim nuevo del JWT)
//   2. user.company.operationsCountryId          (claim nuevo del JWT)
//   3. GET /logistics-companies/:id  ó  GET /companies/:id  en runtime
//      — OBLIGATORIO: los tokens ya emitidos no traen el claim nuevo y siguen
//        vigentes hasta que expiren (precedente: incidente de version skew del
//        personal_token, que produjo un mass-logout).
//   4. Default Chile con console.warn.
//
// La metadata del país (name, code, currency, timezone, divisionLabels,
// postalCodeRates) viene de GET /contracts/countries y se cachea a nivel de
// módulo: el árbol de providers se remonta en cada cambio de ruta (ver App.js),
// así que un cache de estado de React no serviría de nada.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import useAuth from "../hooks/useAuth";
import { getCountries } from "../api/contracts";
import { getLogisticsCompany } from "../api/settings";
import { getCompany } from "../api/company";
import { localeForCountry } from "../views/adminView/cobro/utils/cobroHelpers";

// Id del doc de Chile en la colección `countries`. Es el valor que estaba
// hard-codeado en 13 archivos y sigue siendo el default de último recurso: sin
// él, un operador chileno legado (sin operationsCountryId en su empresa) se
// quedaría sin catálogo de regiones. Deliberadamente NO se exporta: este archivo
// es el único lugar de la app donde debe aparecer.
const CHILE_COUNTRY_ID = "6747d1c9b855f6cb3d1d8afa";

// Valores seguros mientras carga o cuando no se puede resolver el país. Son
// exactamente los que la app asumía antes de esta iniciativa.
const CHILE_DEFAULTS = {
  countryId: CHILE_COUNTRY_ID,
  countryCode: "CL",
  countryName: "Chile",
  currency: "CLP",
  timezone: "America/Santiago",
  locale: "es-CL",
  labels: { region: "Región", commune: "Comuna" },
  postalCodeRates: { enabled: false, formats: [] },
};

// ── Caches de módulo (sobreviven al remontaje del árbol en cada navegación) ──

let countriesPromise = null; // Promise<Array> en vuelo o resuelta
let countriesCache = null; // Array ya resuelto
const companyPromises = new Map(); // `${kind}:${id}` -> Promise<countryId|null>

/** Catálogo de países (metadata sin `regions`). Una sola request por sesión. */
function loadCountries() {
  if (countriesCache) return Promise.resolve(countriesCache);
  if (!countriesPromise) {
    countriesPromise = getCountries()
      .then((data) => {
        if (!Array.isArray(data)) {
          // getCountries no lanza: ante un error devuelve { error }.
          countriesPromise = null;
          console.warn("CountryContext: respuesta inesperada de /contracts/countries", data);
          return [];
        }
        countriesCache = data;
        return countriesCache;
      })
      .catch((err) => {
        // Se limpia la promesa para que un fallo de red no deje el cache
        // envenenado durante toda la sesión.
        countriesPromise = null;
        console.warn("CountryContext: no se pudo cargar el catálogo de países", err);
        return [];
      });
  }
  return countriesPromise;
}

/**
 * Desenvuelve el documento de empresa. Los dos endpoints no comparten forma:
 * `GET /logistics-companies/:id` devuelve el doc pelado y `GET /companies/:id`
 * lo envuelve en `{ data }`; encima `getLogisticsCompany` entrega la respuesta
 * axios completa y `getCompany` solo su body. Se pelan las capas hasta dar con
 * el documento (el que trae `_id`).
 */
function unwrapCompanyDoc(payload) {
  let doc = payload;
  for (let depth = 0; depth < 3 && doc && !doc._id; depth += 1) {
    doc = doc.data;
  }
  return doc && doc._id ? doc : null;
}

/**
 * Fallback runtime: lee el país directamente del documento de la empresa, para
 * los tokens emitidos antes de que el claim existiera. Nunca lanza; devuelve
 * null cuando no se puede determinar.
 */
function loadCompanyCountryId(kind, id) {
  const key = `${kind}:${id}`;
  if (!companyPromises.has(key)) {
    const request = kind === "logistic" ? getLogisticsCompany(id) : getCompany(id);
    companyPromises.set(
      key,
      request
        .then((payload) => {
          if (!payload || payload.error) return null;
          const doc = unwrapCompanyDoc(payload);
          if (!doc) return null;
          const raw = doc.operationsCountryId;
          // El backend puede devolverlo poblado ({_id, name, …}) o plano.
          const value = raw && typeof raw === "object" ? raw._id : raw;
          return value ? String(value) : null;
        })
        .catch((err) => {
          companyPromises.delete(key);
          console.warn("CountryContext: fallback runtime de país falló", err);
          return null;
        }),
    );
  }
  return companyPromises.get(key);
}

const CountryContext = createContext({ ...CHILE_DEFAULTS, loading: true, resolved: false });

export default function CountryProvider({ children }) {
  const auth = useAuth();
  const user = auth?.user ?? null;
  const authLoading = auth?.isLoading !== false;

  // countryId resuelto (null = todavía no se sabe o no se pudo determinar).
  const [countryId, setCountryId] = useState(null);
  const [countries, setCountries] = useState(null);
  const [resolving, setResolving] = useState(true);

  const logisticCompanyId = user?.logisticCompany?._id ?? user?.logisticCompanyId ?? null;
  const companyId = user?.company?._id ?? user?.companyId ?? null;
  const jwtCountryId =
    user?.logisticCompany?.operationsCountryId ?? user?.company?.operationsCountryId ?? null;

  // Catálogo de países. Solo con sesión iniciada: el endpoint viaja por el BFF
  // autenticado, y en /login no hay ningún país que resolver.
  useEffect(() => {
    if (authLoading) return undefined;
    if (!user) {
      setCountries([]);
      return undefined;
    }
    let alive = true;
    loadCountries().then((list) => {
      if (alive) setCountries(list);
    });
    return () => {
      alive = false;
    };
  }, [authLoading, user]);

  // Resolución del país del usuario.
  useEffect(() => {
    if (authLoading) {
      setResolving(true);
      return undefined;
    }

    // Rutas públicas / sesión no iniciada: no hay país que resolver y tampoco
    // hay nada que advertir.
    if (!user) {
      setCountryId(null);
      setResolving(false);
      return undefined;
    }

    if (jwtCountryId) {
      setCountryId(String(jwtCountryId));
      setResolving(false);
      return undefined;
    }

    // El token es anterior al claim: se lee del documento de la empresa.
    const kind = logisticCompanyId ? "logistic" : companyId ? "company" : null;
    const id = logisticCompanyId || companyId;
    if (!kind || !id) {
      setCountryId(null);
      setResolving(false);
      return undefined;
    }

    let alive = true;
    setResolving(true);
    loadCompanyCountryId(kind, String(id)).then((resolvedId) => {
      if (!alive) return;
      setCountryId(resolvedId);
      setResolving(false);
    });
    return () => {
      alive = false;
    };
  }, [authLoading, user, jwtCountryId, logisticCompanyId, companyId]);

  const value = useMemo(() => {
    const loading = authLoading || resolving || countries === null;

    if (loading) {
      return { ...CHILE_DEFAULTS, loading: true, resolved: false };
    }

    if (!countryId) {
      return { ...CHILE_DEFAULTS, loading: false, resolved: false };
    }

    const doc = (countries ?? []).find((c) => String(c?._id) === String(countryId));
    if (!doc) {
      // El id sí se respeta (el catálogo geográfico vive en ese doc); lo que se
      // completa con defaults es la metadata de presentación.
      return { ...CHILE_DEFAULTS, countryId: String(countryId), loading: false, resolved: true };
    }

    const code = String(doc.code || "").toUpperCase();
    return {
      loading: false,
      resolved: true,
      countryId: String(doc._id),
      countryCode: code || CHILE_DEFAULTS.countryCode,
      countryName: doc.name || CHILE_DEFAULTS.countryName,
      currency: doc.currency || CHILE_DEFAULTS.currency,
      timezone: doc.timezone || CHILE_DEFAULTS.timezone,
      locale: localeForCountry(code),
      labels: {
        region: doc.divisionLabels?.region || CHILE_DEFAULTS.labels.region,
        commune: doc.divisionLabels?.commune || CHILE_DEFAULTS.labels.commune,
      },
      postalCodeRates: {
        enabled: doc.postalCodeRates?.enabled === true,
        formats: Array.isArray(doc.postalCodeRates?.formats) ? doc.postalCodeRates.formats : [],
      },
    };
  }, [authLoading, resolving, countries, countryId]);

  // Avisos fuera del render: solo interesan con sesión iniciada (en el login no
  // hay país que resolver y el ruido sería permanente).
  useEffect(() => {
    if (value.loading || !user) return;
    if (!value.resolved) {
      console.warn(
        "CountryContext: la empresa del usuario no tiene país de operación; se usa Chile por defecto.",
      );
    } else if (!(countries ?? []).some((c) => String(c?._id) === String(value.countryId))) {
      console.warn(
        `CountryContext: el país ${value.countryId} no está en el catálogo; se usa la metadata de Chile.`,
      );
    }
  }, [value, user, countries]);

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

/**
 * País de operación del usuario.
 * @returns {{
 *   loading: boolean,
 *   resolved: boolean,
 *   countryId: string,
 *   countryCode: string,
 *   countryName: string,
 *   currency: string,
 *   timezone: string,
 *   locale: string,
 *   labels: { region: string, commune: string },
 *   postalCodeRates: { enabled: boolean, formats: string[] },
 * }}
 *
 * `loading` es true mientras se resuelve: las vistas que disparan fetches con
 * `countryId` deben esperar a que sea false. `resolved` distingue el país real
 * del default de Chile — las vistas que ESCRIBEN datos geográficos (cobertura)
 * no deben operar con `resolved === false`.
 */
export function useCountry() {
  return useContext(CountryContext);
}

export { CountryContext };
